import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Row, Select, Spin, Upload } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { toastError } from "../../../helpers/toasters";
import { getTermsAndConditions, signUpNewApi } from "../../../network";
import { ROUTES } from "../../../constants/_routes";
import { createSignUpSchema, formatSignupMobile } from "./signupSchema";

import logoIcon from "../../../assets/images/logo.png";
import egFlag from "../../../assets/icons/egypt-flag.svg";
import aeFlag from "../../../assets/icons/emirates-flag.svg";

import GlobalModal from "../GlobalModal";
import LanguageBtn from "../../Common/LanguageBtn/LanguageBtn";
import TermAndConditionsModal from "./TermsAndConditionsModal";

function SignUpNew() {
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [terms, setTerms] = useState("");
  const { authorization } = useSelector((state) => state?.auth);
  const { dir } = useSelector((state) => state.language);
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const signUpSchema = useMemo(() => createSignUpSchema(t), [t]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(signUpSchema),
    defaultValues: {
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      mobile: "",
      selectedCountry: "eg",
      idNumber: "",
      salesId: "",
      isTermsAccepted: false,
      image: null,
    },
    mode: "onChange",
  });

  const selectedCountry = watch("selectedCountry");
  const mobile = watch("mobile");
  const imageFile = watch("image");

  const imageFileList = imageFile
    ? [
        {
          uid: "-1",
          name: imageFile.name,
          status: "done",
          originFileObj: imageFile,
        },
      ]
    : [];

  useEffect(() => {
    if (authorization.token) {
      if (authorization?.isEmirate) {
        navigate(ROUTES.BUYANDSUPPLY, { replace: true });
      } else {
        navigate("/home", { replace: true });
      }
    } else {
      setIsAuthenticated(false);
    }
  }, [authorization, navigate]);

  useEffect(() => {
    getTermsAndConditions(
      (success) => {
        if (success.success) {
          setTerms(success.data);
        } else {
          toastError(success.message);
        }
      },
      (fail) => {
        toastError(fail?.data?.error);
      },
    );
  }, [dir]);

  const handlePhoneKeyDown = (e) => {
    if (
      e.key === "Backspace" ||
      (e.key.startsWith("Arrow") &&
        e.key !== "ArrowUp" &&
        e.key !== "ArrowDown")
    ) {
      return;
    }
    if (/\D/.test(e.key)) {
      e.preventDefault();
    }
  };

  const onSubmit = (values) => {
    setLoading(true);

    const formData = new FormData();
    formData.append(
      "fullName",
      `${values.firstName.trim()} ${values.middleName.trim()} ${values.lastName.trim()}`,
    );
    formData.append("email", values.email.trim());
    formData.append(
      "mobile",
      formatSignupMobile(values.mobile, values.selectedCountry),
    );

    if (values.salesId?.trim()) {
      formData.append("salesId", values.salesId.trim());
    }

    if (values.selectedCountry === "eg") {
      formData.append("idNumber", values.idNumber);
      if (values.image) {
        formData.append("IDNumberImage", values.image);
      }
    }

    signUpNewApi(
      formData,
      (success) => {
        setLoading(false);
        if (success?.success) {
          setIsModalOpen(true);
        } else {
          toastError(success.message);
        }
      },
      (fail) => {
        setLoading(false);
        toastError(fail?.data?.error || fail?.data?.message);
      },
    );
  };

  const onCancel = () => {
    setIsModalOpen(false);
  };

  const onAction = () => {};

  return !isAuthenticated ? (
    <section className="mct-section mct-section-signin d-flex flex-column justify-content-center align-items-center">
      <div className="w-100 fixed-top d-flex align-items-center justify-content-between py-3 px-4">
        <div className="logo">
          <img className="icon" src={logoIcon} alt="logo" />
        </div>
        <LanguageBtn inNavbar={false} />
      </div>
      <div className="contact-us__content">
        <Row justify={"center"} className="wd-100">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="contact-us__form wd-100"
          >
            <h1 className="text-center fsize-8 green-text w-100 mb-4">
              {t("auth.signup")}
            </h1>

            <div
              dir={dir}
              style={{
                display: "flex",
                gap: "20px",
              }}
            >
              <div dir={dir} className="">
                <label className="fsize-9">{t("auth.firstName")}</label>
                <input
                  type="text"
                  {...register("firstName")}
                  className={`contact-us__form-input outline-0 fsize-8 ${
                    errors.firstName
                      ? "border border-1 border-danger"
                      : "border-0"
                  }`}
                />
                {errors.firstName && (
                  <span className="contact-us__field-error fsize-10 mt-1">
                    {errors.firstName.message}
                  </span>
                )}
              </div>

              <div dir={dir} className="">
                <label className="fsize-9">{t("auth.middleName")}</label>
                <input
                  type="text"
                  {...register("middleName")}
                  className={`contact-us__form-input outline-0 fsize-8 ${
                    errors.middleName
                      ? "border border-1 border-danger"
                      : "border-0"
                  }`}
                />
                {errors.middleName && (
                  <span className="contact-us__field-error fsize-10 mt-1">
                    {errors.middleName.message}
                  </span>
                )}
              </div>
            </div>

            <div dir={dir} className="contact-us__form-field">
              <label className="fsize-9">{t("auth.lastName")}</label>
              <input
                type="text"
                {...register("lastName")}
                className={`contact-us__form-input outline-0 fsize-8 ${
                  errors.lastName ? "border border-1 border-danger" : "border-0"
                }`}
              />
              {errors.lastName && (
                <span className="contact-us__field-error fsize-10 mt-1">
                  {errors.lastName.message}
                </span>
              )}
            </div>

            <div dir={dir} className="contact-us__form-field my-3">
              <label className="fsize-9">{t("auth.phone")}</label>
              <div
                className={`${
                  dir === "rtl" ? "ps-0" : "pe-0"
                } py-0 d-flex align-items-center gap-1 contact-us__form-input ${
                  errors.mobile ? "border border-1 border-danger" : "border-0"
                }`}
              >
                <Controller
                  name="selectedCountry"
                  control={control}
                  render={({ field }) => (
                    <Select
                      className="form-phone"
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        setValue("mobile", "", { shouldValidate: true });
                        if (value === "ae") {
                          setValue("image", null, { shouldValidate: true });
                          setValue("idNumber", "", { shouldValidate: true });
                        }
                      }}
                      options={[
                        {
                          label: (
                            <div className="d-flex align-items-center gap-2">
                              <img src={egFlag} alt="egypt flag" width="20" />
                              <span>+20</span>
                            </div>
                          ),
                          value: "eg",
                        },
                        {
                          label: (
                            <div className="d-flex align-items-center gap-2">
                              <img
                                src={aeFlag}
                                alt="emirates flag"
                                width="20"
                              />
                              <span>+971</span>
                            </div>
                          ),
                          value: "ae",
                        },
                      ]}
                    />
                  )}
                />

                <div className="flex-fill">
                  <input
                    type="text"
                    {...register("mobile")}
                    onKeyDown={handlePhoneKeyDown}
                    className="contact-us__form-input ps-2 outline-0 fsize-8 border-0"
                  />
                </div>
              </div>

              {errors.mobile && (
                <span className="contact-us__field-error fsize-10 mt-1">
                  {errors.mobile.message}
                </span>
              )}
            </div>

            <div dir={dir} className="contact-us__form-field mb-3">
              <label className="fsize-9">{t("auth.email")}</label>
              <input
                type="text"
                {...register("email")}
                className={`contact-us__form-input fsize-8 outline-0 ${
                  errors.email ? "border border-1 border-danger" : "border-0"
                } `}
              />
              {errors.email && (
                <span className="contact-us__field-error fsize-10">
                  {errors.email.message}
                </span>
              )}
            </div>

            {selectedCountry === "eg" && (
              <>
                <div dir={dir} className="contact-us__form-field mb-3">
                  <label className="fsize-9">{t("auth.idNumber")}</label>
                  <input
                    type="text"
                    {...register("idNumber")}
                    onKeyDown={handlePhoneKeyDown}
                    maxLength={15}
                    className={`contact-us__form-input fsize-8 outline-0 ${
                      errors.idNumber
                        ? "border border-1 border-danger"
                        : "border-0"
                    }`}
                  />
                  {errors.idNumber && (
                    <span className="contact-us__field-error fsize-10">
                      {errors.idNumber.message}
                    </span>
                  )}
                </div>

                <div dir={dir} className="contact-us__form-field mb-3">
                  <label className="fsize-9">{t("auth.idImage")}</label>
                  <Controller
                    name="image"
                    control={control}
                    render={({ field }) => (
                      <Upload
                        accept="image/*"
                        listType="picture-card"
                        maxCount={1}
                        fileList={imageFileList}
                        beforeUpload={(file) => {
                          field.onChange(file);
                          return false;
                        }}
                        onRemove={() => {
                          field.onChange(null);
                        }}
                        className={`signup-id-upload ${
                          errors.image ? "signup-id-upload--error" : ""
                        }`}
                      >
                        {!imageFile && (
                          <div>
                            <PlusOutlined />
                            <div className="mt-2 fsize-10">
                              {t("auth.uploadIdImage")}
                            </div>
                          </div>
                        )}
                      </Upload>
                    )}
                  />
                  {errors.image && (
                    <span className="contact-us__field-error fsize-10">
                      {errors.image.message}
                    </span>
                  )}
                </div>
              </>
            )}

            <div dir={dir} className="contact-us__form-field mb-3">
              <label className="fsize-9">{t("auth.salesId")}</label>
              <input
                type="text"
                {...register("salesId")}
                maxLength={6}
                className={`contact-us__form-input fsize-8 outline-0 ${
                  errors.salesId ? "border border-1 border-danger" : "border-0"
                }`}
              />
              <p className="contact-us__field-hint fsize-10 mt-1 mb-0 text-muted">
                {t("auth.salesIdHint")}
              </p>
              {errors.salesId && (
                <span className="contact-us__field-error fsize-10">
                  {errors.salesId.message}
                </span>
              )}
            </div>

            <div
              dir={dir}
              className={`w-100 d-flex align-items-center gap-2 my-2`}
            >
              <input
                type="checkbox"
                id="terms-checkbox"
                className={`${errors.isTermsAccepted ? "error" : ""} auth-checkbox`}
                {...register("isTermsAccepted")}
              />
              <label className="fsize-10" htmlFor="terms-checkbox">
                {t("auth.accept")}{" "}
                <span
                  onClick={() => setIsTermsModalOpen(true)}
                  className="terms-text text-underline"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setIsTermsModalOpen(true);
                  }}
                >
                  {t("auth.termsAndConditions")}
                </span>
              </label>
            </div>
            {errors.isTermsAccepted && (
              <span className="contact-us__field-error fsize-10 d-block">
                {errors.isTermsAccepted.message}
              </span>
            )}

            <div className="sign-in-btns mt-2 mt-md-4 d-flex gap-3 flex-column justify-content-between align-items-center w-100">
              <Button
                loading={loading}
                className="sign-in-btn border-0 fsize-9 h-auto"
                htmlType="submit"
              >
                {t("auth.signup")}
              </Button>
              <div className="sign-up-btn fsize-9">
                <span className="white-text">{t("auth.withAccount")} </span>
                <Link
                  to={ROUTES.SIGNIN}
                  className="fw-semibold green-text h-auto"
                >
                  {t("auth.login")}
                </Link>
              </div>
            </div>
          </form>
        </Row>
        {isModalOpen && (
          <GlobalModal
            name="otp-modal"
            isModalOpen={isModalOpen}
            onCancel={onCancel}
            onAction={onAction}
            data={{
              selectedCountry,
              mobile: formatSignupMobile(mobile, selectedCountry),
              fireBaseToken: "fireBaseToken",
              deviceId: "deviceId",
            }}
          />
        )}
        {isTermsModalOpen && (
          <TermAndConditionsModal
            isOpen={isTermsModalOpen}
            data={terms}
            onCancel={() => setIsTermsModalOpen(false)}
          />
        )}
      </div>
    </section>
  ) : (
    <div>
      <Spin
        size="large"
        className="spin-overlay w-100 h-100 position-absolute top-0 start-0 d-flex justify-content-center align-items-center"
      />
    </div>
  );
}

export default SignUpNew;
